import React from "react";
import { useGlobalFilter, useRowSelect, useTable } from "react-table";
import { IconButton, TablePagination } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Delete, Edit } from "@material-ui/icons";
import { CustomCheckbox } from "../../../../util";
import { Link } from "react-router-dom";
import TablePaginationActions from "@material-ui/core/TablePagination/TablePaginationActions";

const useStyles = makeStyles({
  iconBtn: {
    color: "#CCCCCC",
    margin: 5,
  },
  link: {
    textDecoration: "none",
    color: "#306F9D",
  },
});

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

export default function ProgrammeTable(props) {
  const {
    deleteProgramme,
    passTableRows,
    totalElements,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    disabled,
  } = props;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const classes = useStyles();

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Programme Title (English)",
        accessor: "titleEn",
        Cell: ({ row, cell }) => {
          return (
            <Link
              to={`/hknt/programmes/edit/${row.original.id}`}
              className={classes.link}
            >
              {row.original.titleEn}
            </Link>
          );
        },
      },
      {
        Header: "Programme Title (Chinese)",
        accessor: "titleTc",
        Cell: ({ row, cell }) => {
          return (
            <Link
              to={`/hknt/programmes/edit/${row.original.id}`}
              className={classes.link}
            >
              {row.original.titleTc}
            </Link>
          );
        },
      },
      {
        Header: "Parent Period",
        accessor: "parentPeriod",
        disableGlobalFilter: true,
      },
      {
        Header: "Period",
        accessor: "period",
        disableGlobalFilter: true,
      },
      {
        Header: "Status",
        accessor: "status",
        disableGlobalFilter: true,
      },
      // {
      //   Header: "Last Updated",
      //   accessor: "lastUpdate",
      //   disableGlobalFilter: true,
      // },
    ],
    []
  );
  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: props.data,
    },
    useGlobalFilter,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...getToggleAllRowsSelectedProps(), disabled }}
              />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...row.getToggleRowSelectedProps(), disabled }}
              />
            </div>
          ),
        },
        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IconButton
                className={classes.iconBtn}
                size="small"
                component={CustomLink}
                to={`/hknt/programmes/edit/${row.original.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
              {disabled || (
                <IconButton
                  className={classes.iconBtn}
                  size="small"
                  onClick={() => deleteProgramme(row.original.id)}
                >
                  <Delete fontSize="inherit" />
                </IconButton>
              )}
            </div>
          ),
        },
      ]);
    }
  );

  React.useEffect(() => {
    passTableRows(selectedFlatRows);
  }, [selectedFlatRows, passTableRows]);

  React.useEffect(() => {
    setGlobalFilter(props.globalFilter);
  }, []);

  return (
    <>
      <Paper>
        <Table aria-label="simple table" component="ul" {...getTableProps()}>
          <TableHead component="div">
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableCell
                    {...column.getHeaderProps()}
                    style={{ paddingTop: 6, paddingBottom: 6 }}
                  >
                    {column.render("Header")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <TableCell
                        {...cell.getCellProps()}
                        style={{
                          paddingTop: 6,
                          paddingBottom: 6,
                          color: "#306F9D",
                        }}
                      >
                        {cell.render("Cell")}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
        colSpan={7}
        count={totalElements}
        rowsPerPage={rowsPerPage}
        page={page}
        SelectProps={{
          inputProps: { "aria-label": "rows per page" },
          native: true,
          style: {
            background: "white",
            border: "1px solid #CCCCCC",
            borderRadius: "4px",
          },
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
        labelRowsPerPage="Show rows"
      />
    </>
  );
}
